"use client";
import React from 'react';
import { Card, CardContent } from '@/components/atoms/card';
import { stripHtml } from '@/lib/utils';
import type { ICmsDocumento } from '@/services/documentos/getDocumentosData/type';

interface DocumentCardProps {
    document: ICmsDocumento;
}

export default function DocumentCard({ document }: DocumentCardProps) {
    // Get document URL: prioritize file.url if it exists and is not empty, otherwise use document.url
    const documentUrl = (document.file?.url && document.file.url.trim() !== '') 
        ? document.file.url 
        : (document.url || '');

    const handlePreview = (e: React.MouseEvent) => {
        console.log('🔍 handlePreview called', { 
            fileUrl: document.file?.url, 
            documentUrl: document.url,
            finalUrl: documentUrl 
        });
        e.stopPropagation();
        if (documentUrl) {
            window.open(documentUrl, '_blank');
        }
    };

    const handleSilentDownload = async (e: React.MouseEvent) => {
        console.log('⬇️ handleSilentDownload called', { 
            fileUrl: document.file?.url, 
            documentUrl: document.url,
            finalUrl: documentUrl 
        });
        e.stopPropagation();
        if (!documentUrl) return;
        const url = documentUrl;
        try {
            // Use API proxy to force Content-Disposition: attachment
            const proxyUrl = `/api/download?url=${encodeURIComponent(url)}`;
            const a = window.document.createElement('a');
            a.href = proxyUrl;
            a.setAttribute('download', '');
            a.style.display = 'none';
            window.document.body.appendChild(a);
            a.click();
            window.document.body.removeChild(a);
        } catch (err) {
            // Fallback to native anchor download; may still open if server forces inline
            const a = window.document.createElement('a');
            a.href = url;
            a.setAttribute('download', '');
            a.style.display = 'none';
            window.document.body.appendChild(a);
            a.click();
            window.document.body.removeChild(a);
            console.log(err);
        }
    };



    // Sanitize title: remove HTML, decode &nbsp; to spaces, collapse multiple spaces
    const rawTitle = document.title || 'Título do Documento';
    const titleText = stripHtml(rawTitle)
        .replace(/&nbsp;+/g, ' ')
        .replace(/\s{2,}/g, ' ')
        .trim();

    return (
        <Card className="bg-white rounded-3xl shadow-lg w-full flex flex-col justify-between relative group hover:shadow-xl transition-shadow duration-300">
            <CardContent className="p-4 md:p-6 h-full">
                {/* Desktop Layout: Horizontal */}
                <div className="hidden md:flex justify-between items-center">
                    {/* PDF Icon */}
                    <div className="flex-shrink-0 mr-4">
                        <svg width="46" height="45" viewBox="0 0 46 45" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g clipPath="url(#clip0_2177_14804)">
                                <path d="M11.429 -0.642822C9.83792 -0.642822 8.53613 0.658963 8.53613 2.25003V42.75C8.53613 44.3411 9.83792 45.6429 11.429 45.6429H40.3576C41.9486 45.6429 43.2504 44.3411 43.2504 42.75V10.9286L31.679 -0.642822H11.429Z" fill="#E2E5E7"/>
                                <path d="M34.5716 10.9286H43.2501L31.6787 -0.642822V8.03575C31.6787 9.62682 32.9805 10.9286 34.5716 10.9286Z" fill="#B0B7BD"/>
                                <path d="M43.2499 19.607L34.5713 10.9285H43.2499V19.607Z" fill="#CAD1D8"/>
                                <path d="M37.4643 36.9642C37.4643 37.7597 36.8134 38.4106 36.0179 38.4106H4.19643C3.40089 38.4106 2.75 37.7597 2.75 36.9642V22.4999C2.75 21.7044 3.40089 21.0535 4.19643 21.0535H36.0179C36.8134 21.0535 37.4643 21.7044 37.4643 22.4999V36.9642Z" fill="#F15642"/>
                                <path d="M9.05566 26.7652C9.05566 26.3834 9.35652 25.9668 9.84107 25.9668H12.5126C14.0169 25.9668 15.3708 26.9735 15.3708 28.903C15.3708 30.7313 14.0169 31.7496 12.5126 31.7496H10.5816V33.277C10.5816 33.7862 10.2576 34.074 9.84107 34.074C9.45922 34.074 9.05566 33.7862 9.05566 33.277V26.7652ZM10.5816 27.4234V30.3046H12.5126C13.2879 30.3046 13.9012 29.6205 13.9012 28.903C13.9012 28.0945 13.2879 27.4234 12.5126 27.4234H10.5816Z" fill="white"/>
                                <path d="M17.6344 34.0717C17.2525 34.0717 16.8359 33.8634 16.8359 33.3557V26.786C16.8359 26.3709 17.2525 26.0686 17.6344 26.0686H20.2828C25.568 26.0686 25.4523 34.0717 20.3869 34.0717H17.6344ZM18.3634 27.4803V32.6614H20.2828C23.4056 32.6614 23.5445 27.4803 20.2828 27.4803H18.3634Z" fill="white"/>
                                <path d="M27.3264 27.5743V29.4127H30.2757C30.6923 29.4127 31.1089 29.8293 31.1089 30.2329C31.1089 30.6147 30.6923 30.9272 30.2757 30.9272H27.3264V33.3557C27.3264 33.7607 27.0386 34.0717 26.6336 34.0717C26.1245 34.0717 25.8135 33.7607 25.8135 33.3557V26.786C25.8135 26.3709 26.1259 26.0686 26.6336 26.0686H30.6937C31.2029 26.0686 31.5037 26.3709 31.5037 26.786C31.5037 27.1563 31.2029 27.5729 30.6937 27.5729H27.3264V27.5743Z" fill="white"/>
                                <path d="M36.0183 38.4108H8.53613V39.8572H36.0183C36.8138 39.8572 37.4647 39.2063 37.4647 38.4108V36.9644C37.4647 37.7599 36.8138 38.4108 36.0183 38.4108Z" fill="#CAD1D8"/>
                            </g>
                            <defs>
                                <clipPath id="clip0_2177_14804">
                                    <rect width="46" height="45" fill="white"/>
                                </clipPath>
                            </defs>
                        </svg>
                    </div>

                    {/* Title - categoria */}
                    <div className="flex-1 flex flex-col items-start">
                        <h3 className="text-xl font-normal font-poppins text-[#334155] leading-relaxed line-clamp-3">
                            {titleText}
                        </h3>
                        <div className="px-0 py-0">
                            <span className=" font-normal font-poppins capitalize leading-normal text-[#377396] line-clamp-1 ">
                                {document.cms_sub_categoria_documento?.cms_categoria_documento?.label
                                    ?? 'Sem Categoria'}
                            </span>
                        </div>
                    </div>

                    {/* Preview Button */}
                    <div className="flex items-center justify-center me-4">
                        <button
                            type="button"
                            onClick={handlePreview}
                            className="flex items-center justify-center bg-[#F05A49] hover:bg-[#d94839] rounded-sm text-white px-6 py-2 transition-colors cursor-pointer"
                        >
                            Pré-visualizar
                        </button>
                    </div>

                    {/* Download Button */}
                    <div className="flex flex-col items-center justify-center">
                        <button
                            type="button"
                            onClick={handleSilentDownload}
                            className="w-9 h-9 rounded-sm border border-[#377396] flex items-center justify-center hover:bg-[#377396]/10 transition-colors cursor-pointer"
                            aria-label="Download documento"
                        >
                            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M13.0557 10.2462V12.8527H2.03223V10.2462H1.36133V12.3527C1.36149 13.0508 1.90796 13.59 2.53223 13.59H12.5557C13.1799 13.59 13.7264 13.0508 13.7266 12.3527V10.2462H13.0557ZM7.20898 0.688599V8.52551L6.34863 7.63391L4.5459 5.76575L4.06152 6.26965L7.54395 9.8927L11.0273 6.26965L10.543 5.76575L8.73926 7.63391L7.87891 8.52551V0.688599H7.20898Z" fill="#377396" stroke="#377396" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Mobile Layout: Vertical */}
                <div className="flex md:hidden flex-col gap-3">
                    {/* Icon, Title and Category */}
                    <div className="flex gap-3">
                        {/* PDF Icon - Smaller */}
                        <div className="flex-shrink-0">
                            <svg width="32" height="32" viewBox="0 0 46 45" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g clipPath="url(#clip0_2177_14804)">
                                    <path d="M11.429 -0.642822C9.83792 -0.642822 8.53613 0.658963 8.53613 2.25003V42.75C8.53613 44.3411 9.83792 45.6429 11.429 45.6429H40.3576C41.9486 45.6429 43.2504 44.3411 43.2504 42.75V10.9286L31.679 -0.642822H11.429Z" fill="#E2E5E7"/>
                                    <path d="M34.5716 10.9286H43.2501L31.6787 -0.642822V8.03575C31.6787 9.62682 32.9805 10.9286 34.5716 10.9286Z" fill="#B0B7BD"/>
                                    <path d="M43.2499 19.607L34.5713 10.9285H43.2499V19.607Z" fill="#CAD1D8"/>
                                    <path d="M37.4643 36.9642C37.4643 37.7597 36.8134 38.4106 36.0179 38.4106H4.19643C3.40089 38.4106 2.75 37.7597 2.75 36.9642V22.4999C2.75 21.7044 3.40089 21.0535 4.19643 21.0535H36.0179C36.8134 21.0535 37.4643 21.7044 37.4643 22.4999V36.9642Z" fill="#F15642"/>
                                    <path d="M9.05566 26.7652C9.05566 26.3834 9.35652 25.9668 9.84107 25.9668H12.5126C14.0169 25.9668 15.3708 26.9735 15.3708 28.903C15.3708 30.7313 14.0169 31.7496 12.5126 31.7496H10.5816V33.277C10.5816 33.7862 10.2576 34.074 9.84107 34.074C9.45922 34.074 9.05566 33.7862 9.05566 33.277V26.7652ZM10.5816 27.4234V30.3046H12.5126C13.2879 30.3046 13.9012 29.6205 13.9012 28.903C13.9012 28.0945 13.2879 27.4234 12.5126 27.4234H10.5816Z" fill="white"/>
                                    <path d="M17.6344 34.0717C17.2525 34.0717 16.8359 33.8634 16.8359 33.3557V26.786C16.8359 26.3709 17.2525 26.0686 17.6344 26.0686H20.2828C25.568 26.0686 25.4523 34.0717 20.3869 34.0717H17.6344ZM18.3634 27.4803V32.6614H20.2828C23.4056 32.6614 23.5445 27.4803 20.2828 27.4803H18.3634Z" fill="white"/>
                                    <path d="M27.3264 27.5743V29.4127H30.2757C30.6923 29.4127 31.1089 29.8293 31.1089 30.2329C31.1089 30.6147 30.6923 30.9272 30.2757 30.9272H27.3264V33.3557C27.3264 33.7607 27.0386 34.0717 26.6336 34.0717C26.1245 34.0717 25.8135 33.7607 25.8135 33.3557V26.786C25.8135 26.3709 26.1259 26.0686 26.6336 26.0686H30.6937C31.2029 26.0686 31.5037 26.3709 31.5037 26.786C31.5037 27.1563 31.2029 27.5729 30.6937 27.5729H27.3264V27.5743Z" fill="white"/>
                                    <path d="M36.0183 38.4108H8.53613V39.8572H36.0183C36.8138 39.8572 37.4647 39.2063 37.4647 38.4108V36.9644C37.4647 37.7599 36.8138 38.4108 36.0183 38.4108Z" fill="#CAD1D8"/>
                                </g>
                                <defs>
                                    <clipPath id="clip0_2177_14804">
                                        <rect width="46" height="45" fill="white"/>
                                    </clipPath>
                                </defs>
                            </svg>
                        </div>

                        {/* Title and Category */}
                        <div className="flex-1 flex flex-col">
                            <h3 className="text-base font-normal font-poppins text-[#334155] leading-snug line-clamp-2">
                                {titleText}
                            </h3>
                            <span className="text-sm font-normal font-poppins capitalize text-[#377396] line-clamp-1">
                                {document.cms_sub_categoria_documento?.cms_categoria_documento?.label
                                    ?? 'Sem Categoria'}
                            </span>
                        </div>
                    </div>

                    {/* Buttons Row */}
                    <div className="flex gap-2 items-center">
                        {/* Preview Button - Smaller */}
                        <button
                            type="button"
                            onClick={handlePreview}
                            className="flex-1 flex items-center justify-center bg-[#F05A49] hover:bg-[#d94839] rounded-sm text-white px-3 py-1.5 text-sm transition-colors cursor-pointer"
                        >
                            Pré-visualizar
                        </button>

                        {/* Download Button - Smaller */}
                        <button
                            type="button"
                            onClick={handleSilentDownload}
                            className="w-8 h-8 rounded-sm border border-[#377396] flex items-center justify-center hover:bg-[#377396]/10 transition-colors cursor-pointer"
                            aria-label="Download documento"
                        >
                            <svg width="12" height="12" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M13.0557 10.2462V12.8527H2.03223V10.2462H1.36133V12.3527C1.36149 13.0508 1.90796 13.59 2.53223 13.59H12.5557C13.1799 13.59 13.7264 13.0508 13.7266 12.3527V10.2462H13.0557ZM7.20898 0.688599V8.52551L6.34863 7.63391L4.5459 5.76575L4.06152 6.26965L7.54395 9.8927L11.0273 6.26965L10.543 5.76575L8.73926 7.63391L7.87891 8.52551V0.688599H7.20898Z" fill="#377396" stroke="#377396" />
                            </svg>
                        </button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}